/**
 * Swap Program Debug Menu
 *
 * Debug menu for withdrawing SOL from the swap program PDA.
 * Only visible in development mode.
 */

import { useState, useEffect } from 'react';
import { AnchorProvider } from '@coral-xyz/anchor';
import { PublicKey } from '@solana/web3.js';
import { toast } from 'sonner';
import { Bug, RefreshCw, ArrowDownToLine, X, ChevronDown, ChevronUp } from 'lucide-react';

import config from '@/config';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import {
  getSwapPdaBalance,
  buildSwapWithdrawTx,
  signAndSendSwapWithdraw,
  getSwapConnection,
  SWAP_PROGRAM_ID,
  type SwapPdaBalance,
} from '@/lib/solana/swapProgramWithdraw';

// Show in dev mode OR when SHOW_SWAP_PDA_BALANCE is enabled (for staging testers)
const SHOW_SWAP_DEBUG = config.IS_DEV || (config as { SHOW_SWAP_PDA_BALANCE?: boolean }).SHOW_SWAP_PDA_BALANCE === true;

export function SwapDebugMenu() {
  const { address, isConnected, walletProvider } = useWalletAddress();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [balance, setBalance] = useState<SwapPdaBalance | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [useManualAddress, setUseManualAddress] = useState(false);

  // Don't render unless debug mode or staging with SHOW_SWAP_PDA_BALANCE enabled
  if (!SHOW_SWAP_DEBUG) {
    return null;
  }

  // Get the effective address to use
  const effectiveAddress = useManualAddress && manualAddress ? manualAddress : address;

  // Get PublicKey from address string
  const publicKey = effectiveAddress ? (() => {
    try {
      return new PublicKey(effectiveAddress);
    } catch {
      return null;
    }
  })() : null;

  const fetchBalance = async () => {
    if (!publicKey) {
      console.log('[SwapDebug] No publicKey to fetch balance for');
      return;
    }

    console.log('[SwapDebug] Fetching balance for:', publicKey.toString());
    setIsLoading(true);
    try {
      const balanceData = await getSwapPdaBalance(publicKey);
      console.log('[SwapDebug] Balance data:', balanceData);
      setBalance(balanceData);
    } catch (error) {
      console.error('[SwapDebug] Error fetching swap PDA balance:', error);
      toast.error('Failed to fetch swap PDA balance');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && publicKey) {
      console.log('[SwapDebug] Auto-fetching balance, isConnected:', isConnected, 'address:', effectiveAddress);
      fetchBalance();
    }
  }, [isConnected, effectiveAddress, isOpen]);

  const handleWithdraw = async () => {
    if (!publicKey || !walletProvider?.signTransaction || !walletProvider?.signAllTransactions) {
      toast.error('Wallet not connected or missing signing methods');
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amountLamports = BigInt(Math.floor(amount * 1e9));

    if (balance && amountLamports > BigInt(balance.balanceField)) {
      toast.error(`Amount exceeds balance field (${(balance.balanceField / 1e9).toFixed(6)} SOL)`);
      return;
    }

    setIsWithdrawing(true);
    try {
      // Create provider with mainnet connection
      const connection = getSwapConnection();
      const wallet = {
        publicKey,
        signTransaction: walletProvider.signTransaction.bind(walletProvider),
        signAllTransactions: walletProvider.signAllTransactions.bind(walletProvider),
      };
      const provider = new AnchorProvider(connection, wallet as any, { commitment: 'confirmed' });

      // Build and send transaction
      const tx = await buildSwapWithdrawTx(provider, amountLamports);
      const signature = await signAndSendSwapWithdraw(provider, tx);

      toast.success('Withdrawal successful!', {
        description: `TX: ${signature.slice(0, 8)}...${signature.slice(-8)}`,
        action: {
          label: 'View',
          onClick: () => window.open(`https://solscan.io/tx/${signature}`, '_blank'),
        },
      });

      // Refresh balance
      await fetchBalance();
      setWithdrawAmount('');
    } catch (error: any) {
      console.error('Withdrawal failed:', error);
      toast.error('Withdrawal failed', {
        description: error.message || 'Unknown error',
      });
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleMaxAmount = () => {
    if (balance) {
      // Use balance field (what the program tracks), not raw lamports
      setWithdrawAmount((balance.balanceField / 1e9).toFixed(6));
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg shadow-lg transition-colors"
      >
        <Bug size={18} />
        <span className="text-sm font-medium">Swap Debug</span>
        {isOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>

      {/* Debug Panel */}
      {isOpen && (
        <div className="absolute bottom-12 left-0 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Bug size={16} className="text-orange-500" />
              Swap Program PDA
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          <div className="text-xs text-gray-500 mb-3 break-all">
            Program: {SWAP_PROGRAM_ID.toString().slice(0, 8)}...
          </div>

          {/* Manual Address Toggle */}
          <div className="mb-3">
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={useManualAddress}
                onChange={(e) => setUseManualAddress(e.target.checked)}
                className="rounded"
              />
              Use manual address
            </label>
            {useManualAddress && (
              <input
                type="text"
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                placeholder="Enter wallet address..."
                className="mt-2 w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500"
              />
            )}
          </div>

          {!effectiveAddress ? (
            <div className="text-gray-400 text-sm">
              {useManualAddress ? 'Enter an address above' : 'Connect wallet to view balance'}
            </div>
          ) : (
            <>
              {/* Wallet Address */}
              <div className="text-xs text-gray-400 mb-2">
                Wallet: {effectiveAddress?.slice(0, 8)}...{effectiveAddress?.slice(-8)}
                {useManualAddress && <span className="text-orange-400 ml-1">(manual)</span>}
              </div>

              {/* Balance Display */}
              <div className="bg-gray-800 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">Swap PDA Balance</span>
                  <button
                    onClick={fetchBalance}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-white disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                </div>

                {balance ? (
                  <div className="space-y-1">
                    <div className="text-white font-mono text-lg">
                      {(balance.balanceField / 1e9).toFixed(6)} SOL
                    </div>
                    <div className="text-xs text-gray-500">
                      <div>PDA: {balance.pdaAddress.slice(0, 8)}...{balance.pdaAddress.slice(-8)}</div>
                      <div>Raw: {(balance.rawLamports / 1e9).toFixed(6)} SOL</div>
                      <div>Rent: {(balance.rentExempt / 1e9).toFixed(6)} SOL</div>
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="text-gray-500 text-sm">Loading...</div>
                ) : (
                  <div className="text-gray-500 text-sm">No PDA found</div>
                )}
              </div>

              {/* Withdraw Form */}
              {balance && balance.balanceField > 0 && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="Amount (SOL)"
                      step="0.000001"
                      min="0"
                      className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                    />
                    <button
                      onClick={handleMaxAmount}
                      className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded"
                    >
                      MAX
                    </button>
                  </div>

                  <button
                    onClick={handleWithdraw}
                    disabled={isWithdrawing || !withdrawAmount}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors"
                  >
                    {isWithdrawing ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Withdrawing...
                      </>
                    ) : (
                      <>
                        <ArrowDownToLine size={16} />
                        Withdraw from Swap PDA
                      </>
                    )}
                  </button>

                  <div className="text-xs text-gray-500 text-center">
                    Withdraws from mainnet swap program
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SwapDebugMenu;
